/**
 * AcademyShell — Sprint 30.2
 *
 * Thin, non-invasive identity + breadcrumb strip that establishes
 * "Sleep Health Services / Sleep Reset Academy" context on every
 * primary Sleep Reset Academy route.
 *
 * Design guarantees:
 *   - Adds NO heading (page owns its own H1).
 *   - Adds NO second navigation bar (AcademyTopBar keeps ownership of
 *     Academy-level nav on the 18 pages that already use it).
 *   - Renders `children` untouched — no width, spacing, or background
 *     constraints on page content.
 *   - Bilingual via existing sleepResetAcademyCopy layer (no new
 *     translation infrastructure).
 *   - Reduced-motion aware via the existing FadeIn primitive (entrance
 *     is subtle and non-repeating; reduced mode renders immediately).
 *   - Semantic: <nav aria-label="Sleep Reset Academy"> containing an
 *     ordered breadcrumb list with aria-current on the leaf crumb.
 *   - Zero new dependencies.
 *
 * Scope:
 *   - Applied only to primary Sleep Reset Academy routes (see App.js).
 *   - NEVER applied to /academy/catalog or the legacy DB-backed Academy
 *     routes.
 */

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { getAcademyCopy } from '../../pages/academy/sleepResetAcademyCopy';
import { FadeIn } from '../../experience/primitives/FadeIn';

// Sprint 30.10A — Deep Context Breadcrumb hook.
// Pages that already have safe, page-owned data (e.g., a resolved path
// name, module code, or lesson title) can call
// `useAcademyBreadcrumbs()` to enrich the shell breadcrumb trail. No
// network calls are made here. If the page never sets crumbs, the shell
// falls back to its Sprint 30.2 single-context-crumb behavior.
//
// Implemented via a module-level pub/sub rather than React context so
// deep-context data can be published from *inside* the AcademyShell tree
// (children) without inverting the current App.js wrapping order.
const _crumbsListeners = new Set();
let _crumbsValue = null; // Array<{label:string,to?:string}> | null

const setAcademyBreadcrumbs = (crumbs) => {
  _crumbsValue = crumbs && crumbs.length ? crumbs : null;
  _crumbsListeners.forEach((fn) => fn(_crumbsValue));
};
const getAcademyBreadcrumbs = () => _crumbsValue;
const subscribeAcademyBreadcrumbs = (fn) => {
  _crumbsListeners.add(fn);
  return () => _crumbsListeners.delete(fn);
};

/**
 * Publish deep-context breadcrumb entries from any Academy page that
 * already holds the data safely on-render (no fabrication, no network).
 *
 * Usage:
 *   useAcademyBreadcrumbs([
 *     { label: 'Beginner Path', to: '/academy/sleep-reset/path/beginner' },
 *     { label: 'M6 · PSG Fundamentals' },
 *   ], [pathName, moduleCode]);
 *
 * The trail is cleared automatically on unmount. Pass a `deps` array to
 * re-publish when the underlying data changes.
 */
export const useAcademyBreadcrumbs = (crumbs, deps = []) => {
  React.useEffect(() => {
    setAcademyBreadcrumbs(crumbs);
    return () => setAcademyBreadcrumbs(null);
  }, deps);
};

// Exact-match route → bilingual shell-crumb key.
const EXACT_ROUTE_KEYS = {
  '/academy/sleep-reset/dashboard': 'dashboard',
  '/academy/sleep-reset/profile': 'profile',
  '/academy/sleep-reset/planner': 'planner',
  '/academy/sleep-reset/favorites': 'favorites',
  '/academy/sleep-reset/search': 'search',
  '/academy/sleep-reset/achievements': 'achievements',
  '/academy/sleep-reset/transcript': 'transcript',
  '/academy/sleep-reset/leaderboard': 'leaderboard',
  '/academy/sleep-reset/settings': 'settings',
  '/academy/sleep-reset/portfolio': 'portfolio',
  '/academy/sleep-reset/calendar': 'calendar',
  '/academy/sleep-reset/certifications': 'certifications',
  '/academy/sleep-reset/practice': 'practice',
  '/academy/sleep-reset/shs-ai': 'shsai',
  '/academy/sleep-reset/program-director-dashboard': 'director',
};

// Prefix-match route → bilingual shell-crumb key. Order matters: longer prefixes first.
const PREFIX_ROUTE_KEYS = [
  ['/academy/sleep-reset/certifications/', 'certifications'],
  ['/academy/sleep-reset/certificate/', 'certificate'],
  ['/academy/sleep-reset/mastery/', 'mastery'],
  ['/academy/sleep-reset/path/', 'path'],
  ['/academy/sleep-reset/module/', 'module'],
  ['/academy/sleep-reset/lesson/', 'lesson'],
  ['/academy/sleep-reset/about/', 'about'],
];

const resolveContextKey = (pathname) => {
  if (EXACT_ROUTE_KEYS[pathname]) return EXACT_ROUTE_KEYS[pathname];
  for (const [prefix, key] of PREFIX_ROUTE_KEYS) {
    if (pathname.startsWith(prefix)) return key;
  }
  return null;
};

/**
 * @param {Object} props
 * @param {React.ReactNode} props.children Page content, rendered untouched.
 * @param {Array<{label:string,to?:string}>} [props.crumbs] Optional explicit
 *   trailing breadcrumb crumbs. If omitted, a single context crumb is derived
 *   from the current route (e.g. "Dashboard", "Path", "Lesson"). Never
 *   fabricates deep contextual data — deeper crumbs are deferred to a future
 *   sprint per Sprint 30.2 scope boundary.
 */
export const AcademyShell = ({ children, crumbs }) => {
  const location = useLocation();
  const { language } = useLanguage();
  const copy = getAcademyCopy(language);

  // Sprint 30.10A — Subscribe to any deep-context breadcrumbs published by
  // the child page via useAcademyBreadcrumbs(). Falls back to the Sprint
  // 30.2 single-context crumb when no page has published deeper data.
  const [deepCrumbs, setDeepCrumbs] = React.useState(getAcademyBreadcrumbs());
  React.useEffect(() => subscribeAcademyBreadcrumbs(setDeepCrumbs), []);

  const onAcademyRoot =
    location.pathname === '/academy' || location.pathname === '/academy/sleep-reset';

  const autoKey = onAcademyRoot ? null : resolveContextKey(location.pathname);
  const baseCrumbs =
    crumbs && crumbs.length > 0
      ? crumbs
      : autoKey
        ? [{ label: copy[`shellCrumb_${autoKey}`] || copy.shellCrumb_academyHome }]
        : [];

  // If a deep-context trail was published, replace the single auto crumb
  // (which is now redundant with the deeper trail).
  const resolvedCrumbs =
    deepCrumbs && deepCrumbs.length > 0 && !crumbs
      ? deepCrumbs
      : baseCrumbs;

  return (
    <>
      <FadeIn as="div" className="bg-slate-950 border-b border-white/10" data-testid="academy-shell">
        <div className="container mx-auto px-4 max-w-6xl">
          <nav
            aria-label={copy.shellAriaLabel}
            className="py-2 sm:py-2.5"
            data-testid="academy-shell-nav"
          >
            <ol className="flex items-center flex-wrap gap-x-1.5 gap-y-1 text-[11px] sm:text-xs text-white/60 leading-relaxed">
              <li>
                <Link
                  to="/"
                  className="hover:text-white transition-colors rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-400"
                  data-testid="academy-shell-shs-link"
                >
                  {copy.shellShsLabel}
                </Link>
              </li>
              <li aria-hidden="true" className="text-white/30">
                <ChevronRight className="h-3 w-3" />
              </li>
              <li>
                {onAcademyRoot ? (
                  <span
                    className="text-white font-medium"
                    aria-current="page"
                    data-testid="academy-shell-current-root"
                  >
                    {copy.shellAcademyLabel}
                  </span>
                ) : (
                  <Link
                    to="/academy/sleep-reset"
                    className="text-white/85 hover:text-white font-medium transition-colors rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-400"
                    data-testid="academy-shell-academy-link"
                  >
                    {copy.shellAcademyLabel}
                  </Link>
                )}
              </li>
              {resolvedCrumbs.map((c, i) => {
                const isLast = i === resolvedCrumbs.length - 1;
                return (
                  <React.Fragment key={`${c.label}-${i}`}>
                    <li aria-hidden="true" className="text-white/30">
                      <ChevronRight className="h-3 w-3" />
                    </li>
                    <li className="min-w-0">
                      {c.to && !isLast ? (
                        <Link
                          to={c.to}
                          className="text-white/70 hover:text-white transition-colors rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-400"
                          data-testid={`academy-shell-crumb-${i}`}
                        >
                          {c.label}
                        </Link>
                      ) : (
                        <span
                          className="text-white/90 truncate max-w-[55vw] sm:max-w-none inline-block align-bottom"
                          aria-current={isLast ? 'page' : undefined}
                          data-testid={`academy-shell-crumb-${i}`}
                        >
                          {c.label}
                        </span>
                      )}
                    </li>
                  </React.Fragment>
                );
              })}
            </ol>
          </nav>
        </div>
      </FadeIn>
      {children}
    </>
  );
};

export default AcademyShell;
